import { createContext } from 'react';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export const NOTIFICATION_TYPES = ['email', 'http'] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export type Notification = {
  id: string;
  type?: NotificationType;
  when?: string;
  enabled?: boolean;
  timezone?: string;

  /* For HTTP notifications */
  url?: string;
  httpMethod?: string;
  headers?: Record<string, string | number | boolean>;
  params?: Record<string, string | number | boolean>;
  hmacSecret?: string;

  /* For Email notifications */
  emails?: string[];
};

export interface ValidationConfigProps {
  validationId?: string;
  selectedTab?: string;
  notifications: Notification[];
}

export interface ValidationConfigState extends ValidationConfigProps {
  setSelectedTab: (tab: string) => void;
  addNotification: (notification: Notification) => void;
  deleteNotification: (id: string) => void;
  getNotification: (id: string) => Notification | undefined;
  updateNotification: (
    id: string,
    updates: Partial<Notification> | ((notification: Notification) => Partial<Notification>)
  ) => void;
}

export const createValidationConfigStore = (validationId?: string, initProps?: Partial<ValidationConfigProps>) => {
  const defaultProps = {
    notifications: [],
  };
  const findNotification = (notifications: Notification[], id: string) => {
    return notifications.find((n) => n.id === id);
  };
  return createStore<ValidationConfigState>()(
    devtools(
      immer((set, get) => ({
        validationId,
        ...defaultProps,
        ...initProps,
        setSelectedTab: (tab) =>
          set((state) => {
            state.selectedTab = tab;
          }),
        addNotification: (notification) =>
          set((state) => {
            if (!findNotification(state.notifications, notification.id)) {
              state.notifications.push(notification);
            }
          }),
        getNotification: (id) => {
          return findNotification(get().notifications, id);
        },
        deleteNotification: (id) =>
          set((state) => {
            const index = state.notifications.findIndex((n) => n.id === id);
            if (index !== -1) {
              state.notifications.splice(index, 1);
            }
          }),
        updateNotification: (id, updates) =>
          set((state) => {
            const notification = findNotification(state.notifications, id);
            if (notification) {
              Object.assign(notification, typeof updates === 'function' ? updates(notification) : updates);
            }
          }),
      }))
    )
  );
};

export type ValidationConfigStore = ReturnType<typeof createValidationConfigStore>;

export const ValidationConfigContext = createContext<ValidationConfigStore | null>(null);
